// Which profile context a notification belongs to for a dual-profile user
// (personal Agent profile vs the AgencyAdmin company profile) — W1-02.
//
// The backend does not send an explicit context field, so context derives from
// what the platform already persists per row, in this order of precedence:
//   1. Explicit per-type overrides (product calls that path/category cannot
//      express: club search requests are AGENCY business even though their
//      target route is shared and their category is "Tenders").
//   2. The persisted redirect_path prefix — the most truthful signal for
//      role-split types (e.g. player_assigned_to_agent points the assigned
//      agent at /agent/mysquad but an admin at an agency surface).
//   3. The server category ("Agency" carries the agency_* lifecycle events).
//   4. Type-family prefixes for the personal agent surfaces.
//   5. null = context-neutral/unknown: visible in every view, never triggers
//      a profile switch.
//
// This mirrors the D-011 perspective filter approach in notificationsSlice
// (ONE shared classifier so the bell dropdown, the mobile sidebar and the
// /notifications page cannot drift) and deliberately does NOT invent any new
// notification state — it only reads fields the notify() pipeline persists.
import { ROLES } from "../config/roles";

export const NOTIFICATION_CONTEXT = {
  AGENCY: "agency",
  PERSONAL: "personal",
};

// 1. Explicit overrides. Club -> agency search requests (Trusted Agent flow)
// are the agency's pipeline even though the route is shared by both roles.
const TYPE_CONTEXT_OVERRIDES = {
  agent_search_request_received: NOTIFICATION_CONTEXT.AGENCY,
  agent_search_request_closed: NOTIFICATION_CONTEXT.AGENCY,
};

// 2. Path prefixes. "/agency" surfaces are the company profile;
// "/agent/..." + the agent marketplaces are the personal profile.
const AGENCY_PATH_PREFIXES = ["/agency"];
const PERSONAL_PATH_PREFIXES = [
  "/agent/",
  "/agent-tenders",
  "/scout-requests",
];

// 4. Personal type families (the agent's own business: their requests,
// recommendations, mandates, pitches, chats, meetings, listings).
const PERSONAL_TYPE_PREFIXES = [
  "agent_tender_",
  "agent_request_",
  "scout_",
  "mandate_",
  "pitch_",
  "new_pitch_",
  "last_minute_",
  "message_request_",
  "chat_",
  "meeting_",
  "friendly_",
  "lawyer_",
  "organizer_",
];
const PERSONAL_TYPES = new Set([
  "verification_opened",
  "saved_agent_tender_expiry",
  "new_tender_posted",
  "pinned_tender_active",
]);

// 5. LAST-RESORT type map. Marin, 2026-07-31: "im Agency Profil [sollen] nicht
// beide Notification angezeigt sein sondern nur die für Agency, und im Agent
// Profil nur die für Agent." With the manual All/Agency/Personal tab row gone
// there is no longer an "All" view to fall back on, so a row that lands nowhere
// would simply disappear. Every event the backend actually emits therefore gets
// a home.
//
// Checked LAST, after path and category, precisely so it cannot overrule the
// role-split types: `player_assigned_to_agent` deep-links the assigned agent to
// /agent/mysquad (personal) while the admins' copies ride the Agency category —
// putting such a type in the top-of-chain overrides would misfile it. This map
// only catches rows that no other signal could place.
const TYPE_CONTEXT_FALLBACK = {
  // Trusted-Agent search requests: the agency's pipeline. `received`/`closed`
  // are already in the overrides above; these two are the same conversation.
  agent_search_request_accepted: NOTIFICATION_CONTEXT.AGENCY,
  agent_search_request_declined: NOTIFICATION_CONTEXT.AGENCY,
  // "every agent you picked declined" — the closing beat of the same
  // Trusted-Agent conversation, so it stays with its four siblings.
  // It was emitted all along (dashboard/agent_search_views.py
  // ALL_DECLINED_NTYPE) and resolved to NOTHING, because the hand-typed
  // catalogue guard never knew the type existed.
  agent_search_request_all_declined: NOTIFICATION_CONTEXT.AGENCY,
  // QCS-T8 wave (BE PRs #322-#326, catalogue 108 -> 115): the rest of the same
  // Trusted-Agent conversation. All seven recipients are either the invited
  // agent (agent_search_views._notify_race_lost/_notify_deadline_reminder/
  // _notify_withdrawn, redirect_path _N3_AGENT_INBOX — same as the
  // received/closed overrides above) or the commissioning club's creator
  // profile (_notify_search_started/_notify_invitation_seen/
  // _notify_delivery_received/_notify_nothing_found, redirect_path the club's
  // own request detail — same as accepted/declined/all_declined above). No
  // "agent_search_" prefix rule exists (only the narrower "agent_tender_"/
  // "agent_request_" families are registered), so without a home here these
  // seven resolve to null and vanish from BOTH profile views for any
  // dual-profile (Agent + AgencyAdmin) user — the exact "unplaced row shown to
  // nobody" failure mode this fallback map exists to close.
  agent_search_started: NOTIFICATION_CONTEXT.AGENCY,
  agent_search_request_seen: NOTIFICATION_CONTEXT.AGENCY,
  agent_search_delivery_received: NOTIFICATION_CONTEXT.AGENCY,
  agent_search_nothing_found: NOTIFICATION_CONTEXT.AGENCY,
  agent_search_request_race_lost: NOTIFICATION_CONTEXT.AGENCY,
  agent_search_request_deadline_soon: NOTIFICATION_CONTEXT.AGENCY,
  agent_search_request_withdrawn: NOTIFICATION_CONTEXT.AGENCY,
  // Membership administration is a company act.
  invitation_received: NOTIFICATION_CONTEXT.AGENCY,
  invitation_accepted: NOTIFICATION_CONTEXT.AGENCY,
  invitation_declined: NOTIFICATION_CONTEXT.AGENCY,
  // The personal agent's own book of business.
  // `saved_agent_tender_expiry` is already personal via PERSONAL_TYPES; its two
  // siblings were simply missing from that set.
  saved_agent_tender_verified: NOTIFICATION_CONTEXT.PERSONAL,
  saved_agent_tender_superseded: NOTIFICATION_CONTEXT.PERSONAL,
  pinned_tender_expired: NOTIFICATION_CONTEXT.PERSONAL,
  tender_inactivity_warning: NOTIFICATION_CONTEXT.PERSONAL,
  tender_expired_inactivity: NOTIFICATION_CONTEXT.PERSONAL,
  // Request drafts: a scheduled request went live, and the heads-up the day
  // before it does. Same family as the two above — the owner's own request,
  // deep-linked to their requests list (/club|/staff|/coach/tenders), which
  // matches neither the agency nor the personal PATH prefix, so the type is
  // the only signal that can place them.
  scheduled_request_published: NOTIFICATION_CONTEXT.PERSONAL,
  scheduled_request_goes_live_tomorrow: NOTIFICATION_CONTEXT.PERSONAL,
  // The third outcome of the same schedule: the date came and the request did
  // NOT go live (a trial period that ran out while the draft waited). Same
  // family, same owner, same club-side deep link as its two siblings, so it is
  // placed with them rather than judged by a rule of its own.
  scheduled_request_publish_blocked: NOTIFICATION_CONTEXT.PERSONAL,
  transfer_window_opening: NOTIFICATION_CONTEXT.PERSONAL,
  player_detailed_position_update: NOTIFICATION_CONTEXT.PERSONAL,
  // A request you pitched on or saved was closed by the club. Same marketplace
  // family as new_tender_posted / pinned_tender_* / the two inactivity events
  // above, all of which are the individual's own book of business. Emitted by
  // dashboard/views.py (TENDER_CLOSED_NTYPE) and likewise unclassified until
  // the catalogue stopped being typed by hand.
  tender_closed: NOTIFICATION_CONTEXT.PERSONAL,
  // "<colleague> shared the shortlist X with you in chat" — deep-links into
  // the inbox and belongs to the person who received it, like the rest of the
  // scout/radar family. Emitted by dashboard/club_radar_views.py
  // (RADAR_LIST_SHARED_NTYPE); the `radar_` name matched no prefix rule.
  radar_list_shared: NOTIFICATION_CONTEXT.PERSONAL,
  // Coach/staff mandates. The `mandate_` prefix below never matched these
  // because the key starts with `staff_mandate_` — a prefix oversight, not a
  // product distinction.
  staff_mandate_invite_received: NOTIFICATION_CONTEXT.PERSONAL,
  staff_mandate_accepted: NOTIFICATION_CONTEXT.PERSONAL,
  staff_mandate_declined: NOTIFICATION_CONTEXT.PERSONAL,
  // Messages family, personal throughout.
  staff_application_contact: NOTIFICATION_CONTEXT.PERSONAL,
  // "Your coaching personality can be updated now": the six-month lock on the
  // self-assessment has expired. It is about the recipient's own profile, so it
  // is personal by definition; it can never reach an agency admin, because the
  // backend only emits it to a profile that carries a Coach or Staff row
  // (users/coaching_personality_tasks.sweep_coaching_personality_reminders).
  // The type is the only signal that can place it: the deep link is `/profile`,
  // which is role-neutral and matches neither path prefix, and the category
  // ("Coaching Personality") is not "Agency".
  coaching_personality_update_available: NOTIFICATION_CONTEXT.PERSONAL,
  // club_admin_promoted and club_role_updated are deliberately ABSENT: both
  // events were removed on Marin's decision of 2026-08-03 — role changes
  // notify nothing (no mail, no bell, no event). Do not re-add entries here;
  // the backend can no longer emit either type.
  //
  // ── Phase-4 wave (BE PRs #265–#270, catalogue 108) ────────────────────────
  // A missed call is a messages-family event and lands in the recipient's own
  // inbox (dashboard/chat_call_notifications.py) — personal like the whole
  // chat_ family; `call_` matched no prefix rule.
  call_missed: NOTIFICATION_CONTEXT.PERSONAL,
  // Club-side membership/request lanes: recipients are club roles, which keep
  // the plain unfiltered list (contextForRole -> "all"), so like tender_closed
  // and the scheduled_request_ family these are personal-homed only so the
  // catalogue guard can prove nothing falls between the two agent profiles.
  club_member_left: NOTIFICATION_CONTEXT.PERSONAL,
  club_request_posted: NOTIFICATION_CONTEXT.PERSONAL,
  // Contract/signing lane (notifications/contract_events.py): the contract
  // parties' own pitch business, category 'pitches' throughout — the
  // `contract_` name just matches no prefix rule (pitch_fully_signed does).
  contract_signer_declined: NOTIFICATION_CONTEXT.PERSONAL,
  contract_signing_expired: NOTIFICATION_CONTEXT.PERSONAL,
  // FIFA licence verdicts are EMAIL-ONLY (in_app=False at the emitter,
  // notifications/license_events.py) — a bell row never exists, so this home
  // is purely defensive for the catalogue guard. The licence is the agent's
  // own, hence personal.
  fifa_licence_failed: NOTIFICATION_CONTEXT.PERSONAL,
  fifa_licence_verified: NOTIFICATION_CONTEXT.PERSONAL,
  // Third state of the pin lifecycle; its two siblings live above/in
  // PERSONAL_TYPES (pinned_tender_active) and here (pinned_tender_expired).
  pinned_tender_promoted: NOTIFICATION_CONTEXT.PERSONAL,
  // A player asks the agent for representation — the agent's own book of
  // business (mandate family); deep link is the role-neutral Home banner, so
  // the type is the only signal that can place it.
  representation_request_received: NOTIFICATION_CONTEXT.PERSONAL,
  // Fourth outcome of the schedule family, same owner and club-side deep link
  // as its three siblings above.
  scheduled_request_schedule_removed: NOTIFICATION_CONTEXT.PERSONAL,
};

const startsWithAny = (value, prefixes) =>
  typeof value === "string" && prefixes.some((p) => value.startsWith(p));

export const resolveNotificationContext = (notification) => {
  if (!notification) return null;
  const type = notification.notification_type || "";

  if (TYPE_CONTEXT_OVERRIDES[type]) return TYPE_CONTEXT_OVERRIDES[type];

  const path = notification.redirect_path;
  if (startsWithAny(path, AGENCY_PATH_PREFIXES)) {
    return NOTIFICATION_CONTEXT.AGENCY;
  }
  if (startsWithAny(path, PERSONAL_PATH_PREFIXES)) {
    return NOTIFICATION_CONTEXT.PERSONAL;
  }

  const categoryName =
    notification.category && notification.category.category_name;
  if (String(categoryName || "").toLowerCase() === "agency") {
    return NOTIFICATION_CONTEXT.AGENCY;
  }
  if (type.startsWith("agency_")) return NOTIFICATION_CONTEXT.AGENCY;

  if (PERSONAL_TYPES.has(type) || startsWithAny(type, PERSONAL_TYPE_PREFIXES)) {
    return NOTIFICATION_CONTEXT.PERSONAL;
  }

  if (TYPE_CONTEXT_FALLBACK[type]) return TYPE_CONTEXT_FALLBACK[type];

  return null;
};

// The context filter only makes sense for users who actually own BOTH agent
// profiles — the exact same gate the manual profile switcher uses (Header.js
// canSwitchRoles).
export const canUseNotificationContextFilter = (roles) =>
  Array.isArray(roles) &&
  roles.includes(ROLES.Agent) &&
  roles.includes(ROLES.AgencyAdmin);

// The context a given profile is supposed to see. Marin, 2026-07-30: "Ich will
// einfach einen harten Split — wenn ich im Agentenprofil bin will ich alles zu
// mir als Agent sehen, und das gleiche wenn ich Agency bin." So the view is no
// longer a manual lens the user has to remember; it follows the profile switch.
// Returns "all" for anyone who is not standing in one of the two agent-side
// profiles (club roles, single-profile users) — they keep the plain list.
export const contextForRole = (currentRole) => {
  if (currentRole === ROLES.AgencyAdmin) return NOTIFICATION_CONTEXT.AGENCY;
  if (currentRole === ROLES.Agent) return NOTIFICATION_CONTEXT.PERSONAL;
  return "all";
};

// Apply a context view to a list. "all" (an ineligible user) passes everything
// through; a profile view shows ONLY that profile's rows.
//
// Strict on purpose. An earlier attempt let unplaceable rows through in BOTH
// views as a safety net against silent loss — but that is exactly the "im
// Agency Profil werden beide Notification angezeigt" Marin reported. The safety
// net now sits where it belongs: in the classifier, which gives every emitted
// event a home (TYPE_CONTEXT_FALLBACK), guarded by a test that walks the full
// backend catalogue. A gap is fixed there, never by softening this filter.
export const filterNotificationsByContext = (notifications, context) => {
  if (!context || context === "all") return notifications;
  return notifications.filter(
    (n) => resolveNotificationContext(n) === context
  );
};

// W1-02 part 2: which profile a notification click must land in. Returns a
// role from ROLES when the click needs a profile switch first, null when the
// current profile is already right (or the user/notification is not
// switchable). Reused by the bell surfaces AND the /notifications page so a
// notification click NEVER lands in the wrong context.
export const contextTargetRole = (notification, roles, currentRole) => {
  if (!canUseNotificationContextFilter(roles)) return null;
  // Only mediate between the two agent-side profiles; never hijack a user
  // currently working in a club (or other) perspective.
  if (currentRole !== ROLES.Agent && currentRole !== ROLES.AgencyAdmin) {
    return null;
  }
  const context = resolveNotificationContext(notification);
  if (context === NOTIFICATION_CONTEXT.AGENCY && currentRole !== ROLES.AgencyAdmin) {
    return ROLES.AgencyAdmin;
  }
  if (context === NOTIFICATION_CONTEXT.PERSONAL && currentRole !== ROLES.Agent) {
    return ROLES.Agent;
  }
  return null;
};
